"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
/*
 * Copyright (C) 2017-2019 HERE Europe B.V.
 * Licensed under Apache 2.0, see full license in LICENSE
 * SPDX-License-Identifier: Apache-2.0
 */
const harp_datasource_protocol_1 = require("@here/harp-datasource-protocol");
const harp_geoutils_1 = require("@here/harp-geoutils");
const harp_materials_1 = require("@here/harp-materials");
const harp_text_canvas_1 = require("@here/harp-text-canvas");
const harp_utils_1 = require("@here/harp-utils");
const THREE = require("three");
const AnimatedExtrusionHandler_1 = require("./AnimatedExtrusionHandler");
const ColorCache_1 = require("./ColorCache");
const DecodedTileHelpers_1 = require("./DecodedTileHelpers");
const DepthPrePass_1 = require("./DepthPrePass");
const Statistics_1 = require("./Statistics");
const TextElement_1 = require("./text/TextElement");
const TextElementsRenderer_1 = require("./text/TextElementsRenderer");
const TextStyleCache_1 = require("./text/TextStyleCache");
const Utils_1 = require("./Utils");
/**
 * Minimum estimated size of a JS object.
 */
const MINIMUM_SMALL_OBJECT_SIZE_ESTIMATION = 16;
const MINIMUM_OBJECT_SIZE_ESTIMATION = 100;
/**
 * A default empty [[DecodedTile]], for tiles that must be rendered but do not have any objects.
 */
const defaultEmptyDecodedTile = {
    techniques: [],
    geometries: []
};
/**
 * Compute the memory footprint of `TileFeatureData`.
 */
function getFeatureDataSize(featureData) {
    let numBytes = MINIMUM_OBJECT_SIZE_ESTIMATION;
    if (featureData.ids !== undefined) {
        numBytes += featureData.ids.length * 8;
    }
    if (featureData.starts !== undefined) {
        numBytes += featureData.starts.length * 8;
    }
    if (featureData.objInfos !== undefined) {
        // 16 (estimated) bytes per objInfos
        numBytes += featureData.objInfos.length * MINIMUM_SMALL_OBJECT_SIZE_ESTIMATION;
    }
    return numBytes;
}
exports.getFeatureDataSize = getFeatureDataSize;
/**
 * Compute the memory footprint of `RoadIntersectionData`.
 */
function getRoadIntersectionDataSize(intersectionData) {
    let numBytes = MINIMUM_OBJECT_SIZE_ESTIMATION;
    // 8 bytes per techniqueIndex
    // 8 bytes per start
    // 8 bytes per width
    // 8 bytes per position
    // 100 (estimated) bytes per technique
    const bytesPerEntry = 8 + 8 + 8 + 8 + MINIMUM_OBJECT_SIZE_ESTIMATION;
    const numEntries = intersectionData.techniqueIndex.length;
    numBytes += intersectionData.techniqueIndex.length * bytesPerEntry;
    if (intersectionData.ids !== undefined) {
        numBytes += numEntries * 8;
    }
    if (intersectionData.objInfos !== undefined) {
        // 16 (estimated) bytes per objInfos
        numBytes += numEntries * MINIMUM_SMALL_OBJECT_SIZE_ESTIMATION;
    }
    return numBytes;
}
/**
 * Missing Typedoc
 */
var TileLoaderState;
(function (TileLoaderState) {
    TileLoaderState[TileLoaderState["Initialized"] = 0] = "Initialized";
    TileLoaderState[TileLoaderState["Loading"] = 1] = "Loading";
    TileLoaderState[TileLoaderState["Loaded"] = 2] = "Loaded";
    TileLoaderState[TileLoaderState["Decoding"] = 3] = "Decoding";
    TileLoaderState[TileLoaderState["Ready"] = 4] = "Ready";
    TileLoaderState[TileLoaderState["Canceled"] = 5] = "Canceled";
    TileLoaderState[TileLoaderState["Failed"] = 6] = "Failed";
})(TileLoaderState = exports.TileLoaderState || (exports.TileLoaderState = {}));
/**
 * The SORT_WEIGHT_PATH_LENGTH constants control how the priority of the labels are computed based
 * on the length of the label strings.
 *
 * Consequently, the [[Technique]]s priority is slightly modified while generating
 * [[TextElement]]s from the [[DecodedTile]], to get a more meaningful priority and stable results.
 */
/**
 * Gives [[TextElement]]s with longer paths a higher priority.
 */
const SORT_WEIGHT_PATH_LENGTH = 0.1;
/**
 * The class that holds the tiled data for a [[DataSource]].
 */
class Tile {
    /**
     * Creates a new [[Tile]].
     *
     * @param dataSource The [[DataSource]] that created this [[Tile]].
     * @param tileKey The unique identifier for this [[Tile]]. Currently only up to level 24 is
     * supported, because of the use of the upper bits for the offset.
     * @param offset The optional offset, this is an integer which represents what multiple of 360
     * degrees to shift, only useful for flat projections, hence optional.
     */
    constructor(dataSource, tileKey, offset = 0) {
        this.dataSource = dataSource;
        this.tileKey = tileKey;
        this.offset = offset;
        /**
         * A list of the THREE.js objects stored in this `Tile`.
         */
        this.objects = [];
        /**
         * The optional list of HERE TileKeys of tiles with geometries that cross
         * the boundaries of this `Tile`.
         */
        this.dependencies = new Array();
        /**
         * The bounding box of this `Tile` in world coordinates.
         */
        this.boundingBox = new THREE.Box3();
        /**
         * The center of this `Tile` in world coordinates.
         */
        this.center = new THREE.Vector3();
        /**
         * Keeping some stats for the individual [[Tile]]s to analyze caching behavior.
         *
         * The frame the [[Tile]] was last requested. This is required to know when the given [[Tile]]
         * can be removed from the cache.
         */
        this.frameNumLastRequested = -1;
        /**
         * The frame the `Tile` was first visible.
         */
        this.frameNumVisible = -1;
        /**
         * The last frame this `Tile` has been rendered (or was in the visible set). Used to determine
         * visibility of `Tile` at the end of a frame, if the number is the current frame number, it is
         * visible.
         */
        this.frameNumLastVisible = -1;
        /**
         * After removing from cache, this is the number of frames the `Tile` was visible.
         */
        this.numFramesVisible = 0;
        this.m_disposed = false;
        this.m_forceHasGeometry = undefined;
        /**
         * Prepared text geometries optimized for display.
         */
        this.m_preparedTextPaths = new Array();
        // Used for [[TextElement]]s which the developer defines.
        this.m_userTextElements = [];
        // Used for [[TextElement]]s that are stored in the data, and that are placed explicitly,
        // fading in and out.
        this.m_textElementGroups = new harp_utils_1.GroupedPriorityList();
        // All visible [[TextElement]]s.
        this.m_placedTextElements = new harp_utils_1.GroupedPriorityList();
        // If `true`, the text content of the [[Tile]] changed.
        this.m_textElementsChanged = false;
        this.m_colorMap = new Map();
        this.m_visibleArea = 0;
        // List of owned textures for disposal
        this.m_ownedTextures = new WeakSet();
        this.geoBox = this.dataSource.getTilingScheme().getGeoBox(this.tileKey);
        this.projection.projectBox(this.geoBox, this.boundingBox);
        this.boundingBox.getCenter(this.center);
    }
    /**
     * The visibility status of the [[Tile]]. It is actually visible or planned to become visible.
     */
    get isVisible() {
        return this.frameNumLastRequested === this.dataSource.mapView.frameNumber;
    }
    /**
     * The [[Projection]] currently used by the [[MapView]].
     */
    get projection() {
        return this.dataSource.projection;
    }
    /**
     * The [[MapView]] this `Tile` belongs to.
     */
    get mapView() {
        return this.dataSource.mapView;
    }
    /**
     * Get the currently visible [[TextElement]]s of this `Tile`. This list is continuously
     * modified, and is not designed to be used to store developer-defined [[TextElements]].
     */
    get placedTextElements() {
        return this.m_placedTextElements;
    }
    /*
     * The size of this Tile in system memory.
     */
    get memoryUsage() {
        if (this.m_resourceInfo === undefined) {
            this.computeResourceInfo();
        }
        return this.m_resourceInfo.heapSize;
    }
    /**
     * Compute [[TileResourceInfo]] of this `Tile`. May be using a cached value. The method
     * `invalidateResourceInfo` can be called beforehand to force a recalculation.
     *
     * @returns `TileResourceInfo` for this `Tile`.
     */
    getResourceInfo() {
        if (this.m_resourceInfo === undefined) {
            this.computeResourceInfo();
        }
        return this.m_resourceInfo;
    }
    /**
     * Force invalidation of the cached [[TileResourceInfo]]. Useful after the `Tile` has been
     * modified.
     */
    invalidateResourceInfo() {
        this.m_resourceInfo = undefined;
    }
    /**
     * Add ownership of a texture to this tile. The texture will be disposed if the `Tile` is
     * disposed.
     * @param texture Texture to be owned by the `Tile`
     */
    addOwnedTexture(texture) {
        this.m_ownedTextures.add(texture);
    }
    /**
     * Gets the list of developer-defined [[TextElement]] in this `Tile`. This list is always
     * rendered first.
     */
    get userTextElements() {
        return this.m_userTextElements;
    }
    /**
     * Adds a developer-defined [[TextElement]] to this `Tile`. The [[TextElement]] is always
     * visible, if it's in the map's currently visible area.
     *
     * @param textElement The Text element to add.
     */
    addUserTextElement(textElement) {
        this.m_userTextElements.push(textElement);
        this.textElementsChanged = true;
    }
    /**
     * Removes a developer-defined [[TextElement]] from this `Tile`.
     *
     * @param textElement A developer-defined TextElement to remove.
     * @returns `true` if the element has been removed successfully; `false` otherwise.
     */
    removeUserTextElement(textElement) {
        const foundIndex = this.m_userTextElements.indexOf(textElement);
        if (foundIndex >= 0) {
            this.m_userTextElements.splice(foundIndex, 1);
            this.textElementsChanged = true;
            return true;
        }
        return false;
    }
    /**
     * Adds a [[TextElement]] to this `Tile`, which is added to the visible set of
     * [[TextElement]]s based on the capacity and visibility. The [[TextElement]]'s priority
     * controls if or when it becomes visible.
     *
     * To ensure that a TextElement is visible, use a high value for its priority, such as
     * `Number.MAX_SAFE_INTEGER`. Since the number of visible TextElements is limited by the
     * screen space, not all TextElements are visible at all times.
     *
     * @param textElement The TextElement to add.
     */
    addTextElement(textElement) {
        this.textElementGroups.add(textElement);
        this.textElementsChanged = true;
    }
    /**
     * Removes a [[TextElement]] from this `Tile`. For the element to be removed successfully, the
     * priority of the [[TextElement]] has to be equal to its priority when it was added.
     *
     * @param textElement The TextElement to remove.
     * @returns `true` if the TextElement has been removed successfully; `false` otherwise.
     */
    removeTextElement(textElement) {
        if (this.textElementGroups.remove(textElement)) {
            this.textElementsChanged = true;
            return true;
        }
        return false;
    }
    /**
     * Gets the current [[GroupedPriorityList]] which contains a list of all [[TextElement]]s to be
     * selected and placed for rendering.
     */
    get textElementGroups() {
        return this.m_textElementGroups;
    }
    /**
     * Gets the current modification state for the list of [[TextElement]]s in the `Tile`. If the
     * value is `true` the TextElement is placed for rendering during the next frame.
     */
    get textElementsChanged() {
        return this.m_textElementsChanged;
    }
    set textElementsChanged(changed) {
        this.m_textElementsChanged = changed;
    }
    /**
     * Called by [[VisibleTileSet]] to mark that [[Tile]] is visible and it should prepare geometry.
     */
    prepareForRender() {
        harp_utils_1.assert(this.isVisible);
        // Prevents of unnecessary re creation of objects
        if (this.m_disposed || this.m_decodedTile === undefined) {
            return;
        }
        const decodedTile = this.m_decodedTile;
        if (decodedTile.tileInfo !== undefined) {
            this.roadIntersectionData = this.dataSource.mapView.pickHandler.registerTile(this);
        }
        this.m_decodedTile = undefined;
        // creates an empty plane if there are no objects on it, but it must be rendered
        if (this.m_forceHasGeometry && this.dataSource.addTileBackground) {
            this.clear();
            this.addGroundPlane();
            return;
        }
        setTimeout(() => {
            const stats = Statistics_1.PerformanceStatistics.instance;
            // If the tile has become invisible while being loaded, for example by moving the
            // camera, the tile is not finished and its geometry is not created. This is an
            // optimization for fast camera movements and zooms.
            if (!this.isVisible) {
                // Dispose the tile from the visible set, so it can be reloaded properly next time
                // it is needed.
                this.mapView.visibleTileSet.disposeTile(this);
                if (stats.enabled) {
                    stats.currentFrame.addMessage(`Decoded tile: ${this.dataSource.name} # lvl=${this.tileKey.level} col=${this.tileKey.column} row=${this.tileKey.row} DISCARDED - invisible`);
                }
                return;
            }
            let now = 0;
            if (stats.enabled) {
                now = harp_utils_1.PerformanceTimer.now();
            }
            this.createGeometries(decodedTile);
            if (stats.enabled) {
                const geometryCreationTime = harp_utils_1.PerformanceTimer.now() - now;
                const currentFrame = stats.currentFrame;
                currentFrame.addValue("geometry.geometryCreationTime", geometryCreationTime);
                currentFrame.addValue("geometryCount.numGeometries", decodedTile.geometries.length);
                currentFrame.addValue("geometryCount.numTechniques", decodedTile.techniques.length);
                currentFrame.addValue("geometryCount.numPoiGeometries", decodedTile.poiGeometries !== undefined ? decodedTile.poiGeometries.length : 0);
                currentFrame.addValue("geometryCount.numTextGeometries", decodedTile.textGeometries !== undefined ? decodedTile.textGeometries.length : 0);
                currentFrame.addValue("geometryCount.numTextPathGeometries", decodedTile.textPathGeometries !== undefined
                    ? decodedTile.textPathGeometries.length
                    : 0);
                currentFrame.addMessage(`Decoded tile: ${this.dataSource.name} # lvl=${this.tileKey.level} col=${this.tileKey.column} row=${this.tileKey.row}`);
            }
            this.dataSource.requestUpdate();
        }, 0);
    }
    /**
     * Called before [[MapView]] starts rendering this `Tile`.
     *
     * @param zoomLevel The current zoom level.
     * @returns Returns `true` if this `Tile` should be rendered.
     */
    willRender(_zoomLevel) {
        return true;
    }
    /**
     * Called after [[MapView]] has rendered this `Tile`.
     */
    didRender() {
        // to be overridden by subclasses
    }
    /**
     * Estimated visible area of tile used for sorting the priorities during loading.
     */
    get visibleArea() {
        return this.m_visibleArea;
    }
    set visibleArea(area) {
        this.m_visibleArea = area;
        if (this.tileLoader !== undefined) {
            this.tileLoader.updatePriority(area);
        }
    }
    /**
     * Called after the `Tile` has been decoded.
     *
     * @param decodedTile The [[DecodedTile]].
     */
    createGeometries(decodedTile) {
        this.clear();
        this.preparePois(decodedTile);
        this.createTextElements(decodedTile);
        this.createObjects(decodedTile, this.objects);
        this.countVertices();
    }
    /**
     * Gets the decoded tile; it is removed after geometry handling.
     */
    get decodedTile() {
        return this.m_decodedTile;
    }
    /**
     * Called by [[TileLoader]] when data for geometry is available.
     *
     * @param decodedTile
     */
    setDecodedTile(decodedTile) {
        this.m_decodedTile = decodedTile;
        this.invalidateResourceInfo();
        const stats = Statistics_1.PerformanceStatistics.instance;
        if (stats.enabled && decodedTile.decodeTime !== undefined) {
            stats.currentFrame.addValue("decode.decodingTime", decodedTile.decodeTime);
        }
    }
    /**
     * Called when the default implementation of `dispose()` needs
     * to free the geometry of a `Tile` object.
     *
     * @param object The object that references the geometry.
     * @returns `true` if the geometry can be disposed.
     */
    // tslint:disable-next-line:no-unused-variable
    shouldDisposeObjectGeometry(object) {
        return true;
    }
    /**
     * Called when the default implementation of `dispose()` needs
     * to free a `Tile` object's material.
     *
     * @param object The object referencing the geometry.
     * @returns `true` if the material can be disposed.
     */
    // tslint:disable-next-line:no-unused-variable
    shouldDisposeObjectMaterial(object) {
        return true;
    }
    /**
     * Called when the default implementation of `dispose()` needs
     * to free a Texture that is part of a `Tile` object's material.
     *
     * @param texture The texture about to be disposed.
     * @returns `true` if the texture can be disposed.
     */
    shouldDisposeTexture(texture) {
        return this.m_ownedTextures.has(texture);
    }
    /**
     * Returns `true` if this `Tile` has been disposed.
     */
    get disposed() {
        return this.m_disposed;
    }
    /**
     * MapView checks if this `Tile` is ready to be rendered while culling.
     *
     * By default, MapView checks if the [[objects]] list is not empty. However, you can override
     * this check by manually setting this property.
     */
    get hasGeometry() {
        if (this.m_forceHasGeometry === undefined) {
            return this.objects.length !== 0;
        }
        else {
            return this.m_forceHasGeometry;
        }
    }
    /**
     * Overrides the default value for [[hasGeometry]].
     *
     * @param value A new value for the [[hasGeometry]] flag.
     */
    forceHasGeometry(value) {
        if (value) {
            this.setDecodedTile(defaultEmptyDecodedTile);
        }
        this.m_forceHasGeometry = value;
    }
    /**
     * Gets the [[ITileLoader]] that manages this tile.
     */
    get tileLoader() {
        return this.m_tileLoader;
    }
    /**
     * Sets the [[ITileLoader]] to manage this tile.
     *
     * @param tileLoader A [[ITileLoader]] instance to manage the loading process for this tile.
     */
    set tileLoader(tileLoader) {
        this.m_tileLoader = tileLoader;
    }
    /**
     * Forces the update of this `Tile` geometry.
     */
    reload() {
        this.dataSource.updateTile(this);
    }
    /**
     * Frees the rendering resources allocated by this `Tile`.
     *
     * The default implementation of this method frees the geometries and the materials for all the
     * reachable objects.
     * Textures are freed if they are owned by this `Tile` (i.e. if they where created by this
     * `Tile`or if the ownership was explicitely set to this `Tile` by [[addOwnedTexture]]).
     */
    clear() {
        const disposeMaterial = (material) => {
            Object.getOwnPropertyNames(material).forEach((property) => {
                const materialProperty = material[property];
                if (materialProperty !== undefined && materialProperty instanceof THREE.Texture) {
                    const texture = materialProperty;
                    if (this.shouldDisposeTexture(texture)) {
                        texture.dispose();
                    }
                }
            });
            material.dispose();
        };
        const disposeObject = (object) => {
            if (object.geometry !== undefined && this.shouldDisposeObjectGeometry(object)) {
                object.geometry.dispose();
            }
            if (object.material !== undefined && this.shouldDisposeObjectMaterial(object)) {
                if (object.material instanceof Array) {
                    object.material.forEach((material) => {
                        if (material !== undefined) {
                            disposeMaterial(material);
                        }
                    });
                }
                else {
                    disposeMaterial(object.material);
                }
            }
        };
        this.objects.forEach((rootObject) => {
            rootObject.traverse((object) => {
                disposeObject(object);
            });
            disposeObject(rootObject);
        });
        this.objects.length = 0;
        if (this.m_preparedTextPaths) {
            this.m_preparedTextPaths = [];
        }
        if (this.m_animatedExtrusionTileHandler !== undefined) {
            this.m_animatedExtrusionTileHandler.dispose();
        }
        this.placedTextElements.clear();
        this.textElementGroups.clear();
        this.userTextElements.length = 0;
        this.invalidateResourceInfo();
    }
    /**
     * Disposes this `Tile`, freeing all geometries and materials for the reachable objects.
     */
    dispose() {
        if (this.m_disposed) {
            return;
        }
        if (this.m_tileLoader) {
            this.m_tileLoader.cancel();
            this.m_tileLoader = undefined;
        }
        this.clear();
        this.userTextElements.length = 0;
        this.m_disposed = true;
        // Ensure that tile is removable from tile cache.
        this.frameNumLastRequested = 0;
    }
    /**
     * Gets the prepared text geometries that are optimized for display.
     */
    get preparedTextPaths() {
        return this.m_preparedTextPaths;
    }
    /**
     * Splits the text paths that contain sharp corners.
     *
     * @param textPathGeometries The original path geometries that may have defects.
     */
    prepareTextPaths(textPathGeometries) {
        const processedPaths = new Array();
        const MAX_CORNER_ANGLE = Math.PI / 8;
        const newPaths = textPathGeometries.slice();
        // maximum reuse of variables to reduce allocations
        const p0 = new THREE.Vector2();
        const p1 = new THREE.Vector2();
        const previousTangent = new THREE.Vector2();
        while (newPaths.length > 0) {
            const textPath = newPaths.pop();
            if (textPath === undefined) {
                break;
            }
            let splitIndex = -1;
            for (let i = 0; i < textPath.path.length - 3; i += 3) {
                p0.set(textPath.path[i], textPath.path[i + 1]);
                p1.set(textPath.path[i + 3], textPath.path[i + 4]);
                const tangent = p1.sub(p0).normalize();
                if (i > 0) {
                    const theta = Math.atan2(previousTangent.x * tangent.y - tangent.x * previousTangent.y, tangent.dot(previousTangent));
                    if (Math.abs(theta) > MAX_CORNER_ANGLE) {
                        splitIndex = i;
                        break;
                    }
                }
                previousTangent.set(tangent.x, tangent.y);
            }
            if (splitIndex > 0) {
                // split off the valid first path points with a clone of the path
                const firstTextPath = {
                    path: textPath.path.slice(0, splitIndex + 3),
                    text: textPath.text,
                    // Used for placement priorities only, can be kept although it could also be
                    // recomputed
                    pathLengthSqr: textPath.pathLengthSqr,
                    technique: textPath.technique,
                    featureId: textPath.featureId
                };
                processedPaths.push(firstTextPath);
                // setup a second part with the rest of the path points and process again
                const secondTextPath = {
                    path: textPath.path.slice(splitIndex),
                    text: textPath.text,
                    // Used for placement priorities only, can be kept although it could also be
                    // recomputed
                    pathLengthSqr: textPath.pathLengthSqr,
                    technique: textPath.technique,
                    featureId: textPath.featureId
                };
                newPaths.push(secondTextPath);
            }
            else {
                processedPaths.push(textPath);
            }
        }
        return processedPaths;
    }
    /**
     * Creates [[TextElement]] objects from the decoded tile and list of materials specified. The
     * priorities of the [[TextElement]]s are updated to simplify label placement.
     *
     * @param decodedTile The [[DecodedTile]].
     */
    createTextElements(decodedTile) {
        const displayZoomLevel = Math.floor(this.mapView.zoomLevel);
        if (decodedTile.textPathGeometries !== undefined) {
            this.m_preparedTextPaths = this.prepareTextPaths(decodedTile.textPathGeometries);
            // Compute maximum street length (squared). Longer streets should be labelled first,
            // they have a higher chance of being placed in case the number of text elements is
            // limited.
            let maxPathLengthSqr = 0;
            for (const textPath of this.preparedTextPaths) {
                const technique = decodedTile.techniques[textPath.technique];
                if (!harp_datasource_protocol_1.isTextTechnique(technique)) {
                    continue;
                }
                if (textPath.pathLengthSqr > maxPathLengthSqr) {
                    maxPathLengthSqr = textPath.pathLengthSqr;
                }
            }
            for (const textPath of this.preparedTextPaths) {
                const technique = decodedTile.techniques[textPath.technique];
                if (!harp_datasource_protocol_1.isTextTechnique(technique)) {
                    continue;
                }
                const path = [];
                for (let i = 0; i < textPath.path.length; i += 3) {
                    path.push(new THREE.Vector2(textPath.path[i], textPath.path[i + 1]));
                }
                // Make sorting stable and make pathLengthSqr a differentiator for placement.
                const priority = harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(technique.priority, displayZoomLevel), 0) +
                    (maxPathLengthSqr > 0
                        ? (SORT_WEIGHT_PATH_LENGTH * textPath.pathLengthSqr) / maxPathLengthSqr
                        : 0);
                const textElement = new TextElement_1.TextElement(harp_text_canvas_1.ContextualArabicConverter.instance.convert(textPath.text), path, this.getRenderStyle(technique), this.getLayoutStyle(technique), priority, technique.xOffset !== undefined ? technique.xOffset : 0.0, technique.yOffset !== undefined ? technique.yOffset : 0.0, textPath.featureId, technique.style, harp_datasource_protocol_1.getPropertyValue(technique.fadeNear, displayZoomLevel), harp_datasource_protocol_1.getPropertyValue(technique.fadeFar, displayZoomLevel));
                textElement.minZoomLevel =
                    technique.minZoomLevel !== undefined
                        ? technique.minZoomLevel
                        : this.mapView.minZoomLevel;
                textElement.maxZoomLevel =
                    technique.maxZoomLevel !== undefined
                        ? technique.maxZoomLevel
                        : this.mapView.maxZoomLevel;
                textElement.distanceScale =
                    technique.distanceScale !== undefined
                        ? technique.distanceScale
                        : TextElementsRenderer_1.DEFAULT_TEXT_DISTANCE_SCALE;
                textElement.mayOverlap = technique.mayOverlap === true;
                textElement.reserveSpace = technique.reserveSpace !== false;
                this.addTextElement(textElement);
            }
        }
        if (decodedTile.textGeometries !== undefined) {
            for (const text of decodedTile.textGeometries) {
                if (text.technique === undefined || text.stringCatalog === undefined) {
                    continue;
                }
                const technique = decodedTile.techniques[text.technique];
                if (!harp_datasource_protocol_1.isTextTechnique(technique)) {
                    continue;
                }
                const positions = new THREE.BufferAttribute(new Float32Array(text.positions.buffer), text.positions.itemCount);
                const numPositions = positions.count;
                if (numPositions < 1) {
                    continue;
                }
                const priority = harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(technique.priority, displayZoomLevel), 0);
                for (let i = 0; i < numPositions; ++i) {
                    const x = positions.getX(i);
                    const y = positions.getY(i);
                    const label = text.stringCatalog[text.texts[i]];
                    if (label === undefined) {
                        // skip missing labels
                        continue;
                    }
                    const textElement = new TextElement_1.TextElement(harp_text_canvas_1.ContextualArabicConverter.instance.convert(label), new THREE.Vector2(x, y), this.getRenderStyle(technique), this.getLayoutStyle(technique), priority, technique.xOffset || 0.0, technique.yOffset || 0.0, text.featureId, technique.style);
                    textElement.minZoomLevel =
                        technique.minZoomLevel !== undefined
                            ? technique.minZoomLevel
                            : this.mapView.minZoomLevel;
                    textElement.maxZoomLevel =
                        technique.maxZoomLevel !== undefined
                            ? technique.maxZoomLevel
                            : this.mapView.maxZoomLevel;
                    textElement.mayOverlap = technique.mayOverlap === true;
                    textElement.reserveSpace = technique.reserveSpace !== false;
                    textElement.fadeNear = harp_datasource_protocol_1.getPropertyValue(technique.fadeNear, displayZoomLevel);
                    textElement.fadeFar = harp_datasource_protocol_1.getPropertyValue(technique.fadeFar, displayZoomLevel);
                    this.addTextElement(textElement);
                }
            }
        }
    }
    /**
     * Adds a THREE object to the root of the tile. Sets the owning tiles datasource.name and the
     * tileKey in the `userData` property of the object, such that the tile it belongs to can be
     * identified during picking.
     *
     * @param object The object to add to the root of the tile.
     */
    registerTileObject(object) {
        const userData = object.userData || {};
        userData.tileKey = this.tileKey;
        userData.dataSource = this.dataSource.name;
    }
    createPlane(width, height, planeCenter, colorHex, isVisible) {
        const geometry = new THREE.PlaneGeometry(width, height, 1);
        // TODO cache the material HARP-4207
        const material = new harp_materials_1.MapMeshBasicMaterial({
            color: colorHex,
            visible: isVisible
        });
        const plane = new THREE.Mesh(geometry, material);
        plane.position.copy(planeCenter);
        // Render before everything else
        plane.renderOrder = Number.MIN_SAFE_INTEGER;
        return plane;
    }
    /**
     * Creates `Tile` objects from the decoded tile and list of materials specified.
     *
     * @param decodedTile The [[DecodedTile]].
     * @param objects The current list of tile objects.
     */
    createObjects(decodedTile, objects) {
        const materials = [];
        const displayZoomLevel = this.mapView.zoomLevel;
        if (this.dataSource.addTileBackground) {
            this.addGroundPlane();
        }
        for (const srcGeometry of decodedTile.geometries) {
            const groups = srcGeometry.groups;
            const groupCount = groups.length;
            for (let groupIndex = 0; groupIndex < groupCount;) {
                const group = groups[groupIndex++];
                const start = group.start;
                const techniqueIndex = group.technique;
                const technique = decodedTile.techniques[techniqueIndex];
                let count = group.count;
                // compress consecutive groups
                for (; groupIndex < groupCount && groups[groupIndex].technique === techniqueIndex; ++groupIndex) {
                    if (start + count !== groups[groupIndex].start) {
                        break;
                    }
                    count += groups[groupIndex].count;
                }
                const ObjectCtor = DecodedTileHelpers_1.getObjectConstructor(technique);
                if (ObjectCtor === undefined) {
                    continue;
                }
                let material = materials[techniqueIndex];
                if (material === undefined) {
                    const onMaterialUpdated = (texture) => {
                        this.dataSource.requestUpdate();
                        if (texture !== undefined) {
                            this.addOwnedTexture(texture);
                        }
                    };
                    material = DecodedTileHelpers_1.createMaterial({
                        technique,
                        level: Math.floor(displayZoomLevel),
                        fog: this.mapView.scene.fog !== null
                    }, onMaterialUpdated);
                    if (material === undefined) {
                        continue;
                    }
                    materials[techniqueIndex] = material;
                }
                // Modify the standard textured shader to support height-based coloring.
                if (harp_datasource_protocol_1.isTerrainTechnique(technique) &&
                    technique.heightBasedColors !== undefined &&
                    technique.displacementMap !== undefined) {
                    material.onBeforeCompile = (shader) => {
                        shader.fragmentShader = shader.fragmentShader.replace("#include <map_pars_fragment>", `#include <map_pars_fragment>
    uniform sampler2D displacementMap;
    uniform float displacementScale;
    uniform float displacementBias;`);
                        shader.fragmentShader = shader.fragmentShader.replace("#include <map_fragment>", `#ifdef USE_MAP
    float minElevation = ${harp_geoutils_1.EarthConstants.MIN_ELEVATION.toFixed(1)};
    float maxElevation = ${harp_geoutils_1.EarthConstants.MAX_ELEVATION.toFixed(1)};
    float elevationRange = maxElevation - minElevation;

    float disp = texture2D( displacementMap, vUv ).x * displacementScale + displacementBias;
    vec4 texelColor = texture2D( map, vec2((disp - minElevation) / elevationRange, 0.0) );
    texelColor = mapTexelToLinear( texelColor );
    diffuseColor *= texelColor;
#endif`);
                    };
                }
                const bufferGeometry = new THREE.BufferGeometry();
                srcGeometry.vertexAttributes.forEach((vertexAttribute) => {
                    const buffer = DecodedTileHelpers_1.getBufferAttribute(vertexAttribute);
                    bufferGeometry.addAttribute(vertexAttribute.name, buffer);
                });
                if (srcGeometry.interleavedVertexAttributes !== undefined) {
                    srcGeometry.interleavedVertexAttributes.forEach((attr) => {
                        const ArrayCtor = harp_datasource_protocol_1.getArrayConstructor(attr.type);
                        const buffer = new THREE.InterleavedBuffer(new ArrayCtor(attr.buffer), attr.stride);
                        attr.attributes.forEach((interleavedAttr) => {
                            const attribute = new THREE.InterleavedBufferAttribute(buffer, interleavedAttr.itemSize, interleavedAttr.offset, false);
                            bufferGeometry.addAttribute(interleavedAttr.name, attribute);
                        });
                    });
                }
                if (srcGeometry.index) {
                    bufferGeometry.setIndex(DecodedTileHelpers_1.getBufferAttribute(srcGeometry.index));
                }
                if (!bufferGeometry.getAttribute("normal") && harp_datasource_protocol_1.needsVertexNormals(technique)) {
                    bufferGeometry.computeVertexNormals();
                }
                bufferGeometry.addGroup(start, count);
                if (harp_datasource_protocol_1.isSolidLineTechnique(technique) || harp_datasource_protocol_1.isDashedLineTechnique(technique)) {
                    const lineMaterial = material;
                    lineMaterial.uniforms.opacity.value = material.opacity;
                    if (technique.clipping !== false) {
                        const tileSize = lineMaterial.uniforms.tileSize;
                        const size = new THREE.Vector3();
                        this.boundingBox.getSize(size);
                        tileSize.value.x = size.x;
                        tileSize.value.y = size.y;
                        lineMaterial.defines.TILE_CLIP = 1;
                    }
                }
                // Add polygon offset to the extruded buildings and to the fill area to avoid depth
                // problems when rendering edges.
                const hasExtrudedOutlines = harp_datasource_protocol_1.isExtrudedPolygonTechnique(technique) && srcGeometry.edgeIndex !== undefined;
                const hasFillOutlines = harp_datasource_protocol_1.isFillTechnique(technique) && srcGeometry.edgeIndex !== undefined;
                if (hasExtrudedOutlines || hasFillOutlines) {
                    material.polygonOffset = true;
                    material.polygonOffsetFactor = 0.75;
                    material.polygonOffsetUnits = 4.0;
                }
                // Add the solid line outlines as a separate object.
                const hasSolidLinesOutlines = harp_datasource_protocol_1.isSolidLineTechnique(technique) && technique.secondaryWidth !== undefined;
                const object = new ObjectCtor(bufferGeometry, material);
                object.frustumCulled = false;
                object.renderOrder = technique.renderOrder;
                if (group.renderOrderOffset !== undefined) {
                    object.renderOrder += group.renderOrderOffset;
                }
                if (srcGeometry.uuid !== undefined) {
                    object.userData.geometryId = srcGeometry.uuid;
                }
                if ((harp_datasource_protocol_1.isCirclesTechnique(technique) || harp_datasource_protocol_1.isSquaresTechnique(technique)) &&
                    technique.enablePicking !== undefined) {
                    // tslint:disable-next-line:max-line-length
                    object.enableRayTesting = technique.enablePicking;
                }
                // Lines renderOrder fix: Render them as transparent objects, but make sure they end
                // up in the opaque rendering queue (by disabling transparency onAfterRender, and
                // enabling it onBeforeRender).
                if (harp_datasource_protocol_1.isSolidLineTechnique(technique) || harp_datasource_protocol_1.isDashedLineTechnique(technique)) {
                    const fadingParams = this.getFadingParams(technique);
                    harp_materials_1.FadingFeature.addRenderHelper(object, fadingParams.fadeNear, fadingParams.fadeFar, true, false, (renderer, mat) => {
                        const lineMaterial = mat;
                        const metricUnits = harp_datasource_protocol_1.getPropertyValue(technique.metricUnit, this.tileKey.level);
                        const unitFactor = metricUnits === "Pixel" ? this.mapView.pixelToWorld * 0.5 : 1.0;
                        lineMaterial.lineWidth =
                            harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(technique.lineWidth, this.mapView.zoomLevel), harp_materials_1.SolidLineMaterial.DEFAULT_WIDTH) * unitFactor;
                        // Do the same for dashSize and gapSize for dashed lines.
                        if (harp_datasource_protocol_1.isDashedLineTechnique(technique)) {
                            const dashedLineMaterial = lineMaterial;
                            dashedLineMaterial.dashSize =
                                harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(technique.dashSize, this.mapView.zoomLevel), harp_materials_1.DashedLineMaterial.DEFAULT_DASH_SIZE) * unitFactor;
                            dashedLineMaterial.gapSize =
                                harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(technique.gapSize, this.mapView.zoomLevel), harp_materials_1.DashedLineMaterial.DEFAULT_GAP_SIZE) * unitFactor;
                        }
                    });
                }
                if (harp_datasource_protocol_1.isExtrudedLineTechnique(technique)) {
                    // extruded lines are normal meshes, and need transparency only when fading
                    // is defined.
                    if (technique.fadeFar !== undefined) {
                        const fadingParams = this.getFadingParams(technique);
                        harp_materials_1.FadingFeature.addRenderHelper(object, fadingParams.fadeNear, fadingParams.fadeFar, true, true);
                    }
                }
                this.addFeatureData(srcGeometry, technique, object);
                if (harp_datasource_protocol_1.isExtrudedPolygonTechnique(technique) || harp_datasource_protocol_1.isFillTechnique(technique)) {
                    // filled polygons are normal meshes, and need transparency only when fading is
                    // defined.
                    if (technique.fadeFar !== undefined) {
                        const fadingParams = this.getFadingParams(technique);
                        harp_materials_1.FadingFeature.addRenderHelper(object, fadingParams.fadeNear, fadingParams.fadeFar, true, true);
                    }
                }
                const extrudedObjects = [];
                const animatedExtrusionHandler = this.mapView.animatedExtrusionHandler;
                let extrusionAnimatonEnabled = false;
                if (harp_datasource_protocol_1.isExtrudedPolygonTechnique(technique) &&
                    animatedExtrusionHandler !== undefined) {
                    extrusionAnimatonEnabled =
                        technique.animateExtrusion !== undefined &&
                            animatedExtrusionHandler.forceEnabled === false
                            ? technique.animateExtrusion
                            : animatedExtrusionHandler.enabled;
                }
                const renderDepthPrePass = harp_datasource_protocol_1.isExtrudedPolygonTechnique(technique) && DepthPrePass_1.isRenderDepthPrePassEnabled(technique);
                if (renderDepthPrePass) {
                    const depthPassMesh = DepthPrePass_1.createDepthPrePassMesh(object);
                    objects.push(depthPassMesh);
                    if (extrusionAnimatonEnabled) {
                        extrudedObjects.push({
                            object: depthPassMesh,
                            materialFeature: true
                        });
                    }
                    DepthPrePass_1.setDepthPrePassStencil(depthPassMesh, object);
                }
                this.registerTileObject(object);
                objects.push(object);
                // Add the extruded building edges as a separate geometry.
                if (hasExtrudedOutlines) {
                    const edgeGeometry = new THREE.BufferGeometry();
                    edgeGeometry.addAttribute("position", bufferGeometry.getAttribute("position"));
                    const colorAttribute = bufferGeometry.getAttribute("color");
                    if (colorAttribute !== undefined) {
                        edgeGeometry.addAttribute("color", colorAttribute);
                    }
                    edgeGeometry.setIndex(DecodedTileHelpers_1.getBufferAttribute(srcGeometry.edgeIndex));
                    // Read the uniforms from the technique values (and apply the default values).
                    const extrudedPolygonTechnique = technique;
                    const fadingParams = this.getPolygonFadingParams(extrudedPolygonTechnique);
                    // Configure the edge material based on the theme values.
                    const materialParams = {
                        color: fadingParams.color,
                        colorMix: fadingParams.colorMix,
                        fadeNear: fadingParams.lineFadeNear,
                        fadeFar: fadingParams.lineFadeFar
                    };
                    const edgeMaterial = new harp_materials_1.EdgeMaterial(materialParams);
                    const edgeObj = new THREE.LineSegments(edgeGeometry, edgeMaterial);
                    // Set the correct render order.
                    edgeObj.renderOrder = object.renderOrder + 0.1;
                    harp_materials_1.FadingFeature.addRenderHelper(edgeObj, fadingParams.lineFadeNear, fadingParams.lineFadeFar, false, false);
                    if (extrusionAnimatonEnabled) {
                        extrudedObjects.push({
                            object: edgeObj,
                            materialFeature: false
                        });
                    }
                    this.registerTileObject(edgeObj);
                    objects.push(edgeObj);
                }
                // animate the extrusion of buildings
                if (harp_datasource_protocol_1.isExtrudedPolygonTechnique(technique) && extrusionAnimatonEnabled) {
                    extrudedObjects.push({
                        object,
                        materialFeature: true
                    });
                    const extrusionAnimatonDuration = technique.animateExtrusionDuration !== undefined &&
                        animatedExtrusionHandler.forceEnabled === false
                        ? technique.animateExtrusionDuration
                        : animatedExtrusionHandler.duration;
                    this.m_animatedExtrusionTileHandler = new AnimatedExtrusionHandler_1.AnimatedExtrusionTileHandler(this, extrudedObjects, extrusionAnimatonDuration);
                    this.mapView.animatedExtrusionHandler.add(this.m_animatedExtrusionTileHandler);
                }
                // Add the fill area edges as a separate geometry.
                if (hasFillOutlines) {
                    const edgeIndexBuffers = srcGeometry.edgeIndex;
                    for (const edgeIndexBufferAttribute of edgeIndexBuffers) {
                        const outlineGeometry = new THREE.BufferGeometry();
                        outlineGeometry.addAttribute("position", bufferGeometry.getAttribute("position"));
                        outlineGeometry.setIndex(DecodedTileHelpers_1.getBufferAttribute(edgeIndexBufferAttribute));
                        const fillTechnique = technique;
                        const fadingParams = this.getPolygonFadingParams(fillTechnique);
                        // Configure the edge material based on the theme values.
                        const materialParams = {
                            color: fadingParams.color,
                            colorMix: fadingParams.colorMix,
                            fadeNear: fadingParams.lineFadeNear,
                            fadeFar: fadingParams.lineFadeFar
                        };
                        const outlineMaterial = new harp_materials_1.EdgeMaterial(materialParams);
                        const outlineObj = new THREE.LineSegments(outlineGeometry, outlineMaterial);
                        outlineObj.renderOrder = object.renderOrder + 0.1;
                        harp_materials_1.FadingFeature.addRenderHelper(outlineObj, fadingParams.lineFadeNear, fadingParams.lineFadeFar, true, false);
                        this.registerTileObject(outlineObj);
                        objects.push(outlineObj);
                    }
                }
                // Add the fill area edges as a separate geometry.
                if (hasSolidLinesOutlines) {
                    const outlineTechnique = technique;
                    const outlineMaterial = material.clone();
                    const outlineColor = ColorCache_1.ColorCache.instance.getColor(harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(outlineTechnique.secondaryColor, Math.floor(this.mapView.zoomLevel)), "#000000"));
                    outlineMaterial.uniforms.diffuse.value = outlineColor;
                    const outlineObj = new ObjectCtor(bufferGeometry, outlineMaterial);
                    outlineObj.renderOrder =
                        outlineTechnique.secondaryRenderOrder !== undefined
                            ? outlineTechnique.secondaryRenderOrder
                            : technique.renderOrder - 0.0000001;
                    if (group.renderOrderOffset !== undefined) {
                        outlineObj.renderOrder += group.renderOrderOffset;
                    }
                    const fadingParams = this.getFadingParams(technique);
                    harp_materials_1.FadingFeature.addRenderHelper(outlineObj, fadingParams.fadeNear, fadingParams.fadeFar, true, false, (renderer, mat) => {
                        const lineMaterial = mat;
                        const metricUnits = harp_datasource_protocol_1.getPropertyValue(outlineTechnique.metricUnit, this.tileKey.level);
                        const unitFactor = metricUnits === "Pixel" ? this.mapView.pixelToWorld * 0.5 : 1.0;
                        lineMaterial.lineWidth =
                            harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(outlineTechnique.secondaryWidth, this.mapView.zoomLevel), harp_materials_1.SolidLineMaterial.DEFAULT_WIDTH) * unitFactor;
                    });
                    this.registerTileObject(outlineObj);
                    objects.push(outlineObj);
                }
            }
        }
    }
    /**
     * Counts the number of vertices in a tile.
     */
    countVertices() {
        let numVertices = 0;
        this.objects
            .filter(object => object instanceof THREE.Mesh)
            .forEach(object => {
            const mesh = object;
            if (mesh.geometry instanceof THREE.BufferGeometry) {
                if (mesh.geometry.index !== undefined && mesh.geometry.index !== null) {
                    numVertices += mesh.geometry.index.count;
                }
                else {
                    numVertices += mesh.geometry.getAttribute("position").count / 3;
                }
            }
            if (mesh.geometry instanceof THREE.Geometry) {
                numVertices = mesh.geometry.vertices.length;
            }
        });
        return numVertices;
    }
    /**
     * Missing Typedoc
     */
    preparePois(decodedTile) {
        if (decodedTile.poiGeometries !== undefined) {
            this.mapView.poiManager.addPois(this, decodedTile);
        }
    }
    /**
     * Gets the appropriate [[TextRenderStyle]] to use for a label. Depends heavily on the label's
     * [[Technique]] and the current zoomLevel.
     *
     * @param technique Label's technique.
     * @param techniqueIdx Label's technique index.
     */
    getRenderStyle(technique) {
        const cacheId = TextStyleCache_1.computeStyleCacheId(this.dataSource.name, technique, this.mapView.zoomLevel);
        let renderStyle = this.mapView.textRenderStyleCache.get(cacheId);
        if (renderStyle === undefined) {
            const defaultRenderParams = this.mapView.textElementsRenderer !== undefined
                ? this.mapView.textElementsRenderer.defaultStyle.renderParams
                : {
                    fontSize: {
                        unit: harp_text_canvas_1.FontUnit.Pixel,
                        size: 32,
                        backgroundSize: 8
                    }
                };
            const textSize = harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(technique.size, Math.floor(this.mapView.zoomLevel)), defaultRenderParams.fontSize.size);
            const bgSize = harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(technique.backgroundSize, Math.floor(this.mapView.zoomLevel)), defaultRenderParams.fontSize.backgroundSize);
            const hexColor = harp_datasource_protocol_1.getPropertyValue(technique.color, Math.floor(this.mapView.zoomLevel));
            if (hexColor !== undefined) {
                this.m_colorMap.set(cacheId, ColorCache_1.ColorCache.instance.getColor(hexColor));
            }
            const styleColor = harp_utils_1.getOptionValue(this.m_colorMap.get(cacheId), defaultRenderParams.color);
            const hexBgColor = harp_datasource_protocol_1.getPropertyValue(technique.backgroundColor, Math.floor(this.mapView.zoomLevel));
            if (hexBgColor !== undefined) {
                this.m_colorMap.set(cacheId + "_bg", ColorCache_1.ColorCache.instance.getColor(hexBgColor));
            }
            const styleBgColor = harp_utils_1.getOptionValue(this.m_colorMap.get(cacheId + "_bg"), defaultRenderParams.backgroundColor);
            const bgAlpha = harp_utils_1.getOptionValue(harp_datasource_protocol_1.getPropertyValue(technique.backgroundAlpha, Math.floor(this.mapView.zoomLevel)), defaultRenderParams.backgroundOpacity);
            const isSmallCaps = technique.smallCaps === true ||
                defaultRenderParams.fontVariant === harp_text_canvas_1.FontVariant.SmallCaps;
            const isAllCaps = technique.allCaps === true ||
                defaultRenderParams.fontVariant === harp_text_canvas_1.FontVariant.AllCaps;
            const styleFontVariant = isSmallCaps
                ? harp_text_canvas_1.FontVariant.SmallCaps
                : isAllCaps
                    ? harp_text_canvas_1.FontVariant.AllCaps
                    : undefined;
            const styleFontStyle = technique.bold === true
                ? technique.oblique
                    ? harp_text_canvas_1.FontStyle.BoldItalic
                    : harp_text_canvas_1.FontStyle.Bold
                : technique.oblique === true
                    ? harp_text_canvas_1.FontStyle.Italic
                    : defaultRenderParams.fontStyle;
            const renderParams = {
                fontSize: {
                    unit: harp_text_canvas_1.FontUnit.Pixel,
                    size: textSize,
                    backgroundSize: bgSize
                },
                color: styleColor,
                backgroundColor: styleBgColor,
                backgroundOpacity: bgAlpha,
                fontVariant: styleFontVariant,
                fontStyle: styleFontStyle
            };
            const themeRenderParams = this.mapView.textElementsRenderer !== undefined
                ? this.mapView.textElementsRenderer.getTextElementStyle(technique.style)
                    .renderParams
                : {};
            renderStyle = new harp_text_canvas_1.TextRenderStyle(Object.assign({}, themeRenderParams, renderParams));
            this.mapView.textRenderStyleCache.set(cacheId, renderStyle);
        }
        return renderStyle;
    }
    /**
     * Gets the appropriate [[TextRenderStyle]] to use for a label. Depends heavily on the label's
     * [[Technique]] and the current zoomLevel.
     *
     * @param technique Label's technique.
     */
    getLayoutStyle(technique) {
        const cacheId = TextStyleCache_1.computeStyleCacheId(this.dataSource.name, technique, this.mapView.zoomLevel);
        let layoutStyle = this.mapView.textLayoutStyleCache.get(cacheId);
        if (layoutStyle === undefined) {
            const defaultLayoutParams = this.mapView.textElementsRenderer !== undefined
                ? this.mapView.textElementsRenderer.defaultStyle.layoutParams
                : {};
            const trackingFactor = harp_utils_1.getOptionValue(technique.tracking, defaultLayoutParams.tracking);
            let hAlignment = defaultLayoutParams.horizontalAlignment;
            if (technique.hAlignment === "Left" ||
                technique.hAlignment === "Center" ||
                technique.hAlignment === "Right") {
                hAlignment = harp_text_canvas_1.HorizontalAlignment[technique.hAlignment];
            }
            let vAlignment = defaultLayoutParams.verticalAlignment;
            if (technique.vAlignment === "Above" ||
                technique.vAlignment === "Center" ||
                technique.vAlignment === "Below") {
                vAlignment = harp_text_canvas_1.VerticalAlignment[technique.vAlignment];
            }
            const layoutParams = {
                tracking: trackingFactor,
                horizontalAlignment: hAlignment,
                verticalAlignment: vAlignment
            };
            const themeLayoutParams = this.mapView.textElementsRenderer !== undefined
                ? this.mapView.textElementsRenderer.getTextElementStyle(technique.style)
                    .layoutParams
                : {};
            layoutStyle = new harp_text_canvas_1.TextLayoutStyle(Object.assign({}, themeLayoutParams, layoutParams));
            this.mapView.textLayoutStyleCache.set(cacheId, layoutStyle);
        }
        return layoutStyle;
    }
    computeResourceInfo() {
        let heapSize = 0;
        let num3dObjects = 0;
        let numTextElements = 0;
        let numUserTextElements = 0;
        const aggregatedObjSize = {
            heapSize: 0,
            gpuSize: 0
        };
        // Keep a map of the uuids of the larger objects, like Geometries, Materials and Attributes.
        // They should be counted only once even if they are shared.
        const visitedObjects = new Map();
        for (const object of this.objects) {
            if (object.visible) {
                num3dObjects++;
            }
            Utils_1.MapViewUtils.estimateObject3dSize(object, aggregatedObjSize, visitedObjects);
        }
        for (const group of this.textElementGroups.groups) {
            numTextElements += group[1].elements.length;
        }
        numUserTextElements = this.userTextElements.length;
        // 216 was the shallow size of a single TextElement last time it has been checked, 312 bytes
        // was the minimum retained size of a TextElement that was not being rendered. If a
        // TextElement is actually rendered, the size may be _much_ bigger.
        heapSize += (numTextElements + numUserTextElements) * 312;
        if (this.m_decodedTile !== undefined && this.m_decodedTile.tileInfo !== undefined) {
            aggregatedObjSize.heapSize += this.m_decodedTile.tileInfo.numBytes;
        }
        if (this.roadIntersectionData !== undefined) {
            heapSize += getRoadIntersectionDataSize(this.roadIntersectionData);
        }
        this.m_resourceInfo = {
            heapSize: aggregatedObjSize.heapSize + heapSize,
            gpuSize: aggregatedObjSize.gpuSize,
            num3dObjects,
            numTextElements,
            numUserTextElements
        };
    }
    /**
     * Pass the feature data on to the object, so it can be used in picking
     * `MapView.intersectMapObjects()`. Do not pass the feature data if the technique is a
     * dashed-line or a solid-line, because the line picking functionality for the lines is not
     * object based, but tile based.
     *
     * @param srcGeometry The original [[Geometry]].
     * @param technique The corresponding [[Technique]].
     * @param object The object to pass info to.
     */
    addFeatureData(srcGeometry, technique, object) {
        if (((srcGeometry.featureIds !== undefined && srcGeometry.featureIds.length > 0) ||
            harp_datasource_protocol_1.isCirclesTechnique(technique) ||
            harp_datasource_protocol_1.isSquaresTechnique(technique)) &&
            !harp_datasource_protocol_1.isSolidLineTechnique(technique) &&
            !harp_datasource_protocol_1.isDashedLineTechnique(technique)) {
            const featureData = {
                geometryType: srcGeometry.type,
                ids: srcGeometry.featureIds,
                starts: srcGeometry.featureStarts
            };
            object.userData.feature = featureData;
            if (srcGeometry.objInfos !== undefined) {
                object.userData.feature.objInfos = srcGeometry.objInfos;
            }
        }
    }
    /**
     * Gets the fading parameters for several kinds of objects.
     */
    getFadingParams(technique) {
        const displayZoomLevel = this.mapView.zoomLevel;
        const fadeNear = technique.fadeNear !== undefined
            ? harp_datasource_protocol_1.getPropertyValue(technique.fadeNear, displayZoomLevel)
            : harp_materials_1.FadingFeature.DEFAULT_FADE_NEAR;
        const fadeFar = technique.fadeFar !== undefined
            ? harp_datasource_protocol_1.getPropertyValue(technique.fadeFar, displayZoomLevel)
            : harp_materials_1.FadingFeature.DEFAULT_FADE_FAR;
        return {
            fadeNear,
            fadeFar
        };
    }
    /**
     * Gets the fading parameters for several kinds of objects.
     */
    getPolygonFadingParams(technique) {
        let color = harp_materials_1.EdgeMaterial.DEFAULT_COLOR;
        let colorMix = harp_materials_1.EdgeMaterial.DEFAULT_COLOR_MIX;
        if (technique.lineColor !== undefined) {
            color = technique.lineColor;
            if (harp_datasource_protocol_1.isExtrudedPolygonTechnique(technique)) {
                const extrudedPolygonTechnique = technique;
                colorMix =
                    extrudedPolygonTechnique.lineColorMix !== undefined
                        ? extrudedPolygonTechnique.lineColorMix
                        : harp_materials_1.EdgeMaterial.DEFAULT_COLOR_MIX;
            }
        }
        const displayZoomLevel = this.mapView.zoomLevel;
        const fadeNear = technique.fadeNear !== undefined
            ? harp_datasource_protocol_1.getPropertyValue(technique.fadeNear, displayZoomLevel)
            : harp_materials_1.FadingFeature.DEFAULT_FADE_NEAR;
        const fadeFar = technique.fadeFar !== undefined
            ? harp_datasource_protocol_1.getPropertyValue(technique.fadeFar, displayZoomLevel)
            : harp_materials_1.FadingFeature.DEFAULT_FADE_FAR;
        const lineFadeNear = technique.lineFadeNear !== undefined
            ? harp_datasource_protocol_1.getPropertyValue(technique.lineFadeNear, displayZoomLevel)
            : fadeNear;
        const lineFadeFar = technique.lineFadeFar !== undefined
            ? harp_datasource_protocol_1.getPropertyValue(technique.lineFadeFar, displayZoomLevel)
            : fadeFar;
        return {
            color,
            colorMix,
            fadeNear,
            fadeFar,
            lineFadeNear,
            lineFadeFar
        };
    }
    /**
     * Creates and add a background plane for the tile.
     */
    addGroundPlane() {
        // Add a ground plane to the tile.
        const planeSize = new THREE.Vector3();
        this.boundingBox.getSize(planeSize);
        const groundPlane = this.createPlane(planeSize.x, planeSize.y, this.center, this.dataSource.tileBackgroundColor === undefined
            ? this.mapView.clearColor
            : this.dataSource.tileBackgroundColor, this.dataSource.tileBackgroundIsVisible);
        this.registerTileObject(groundPlane);
        this.objects.push(groundPlane);
    }
}
exports.Tile = Tile;
